import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { getAccessToken, refreshAccessToken } from '../auth/session';
import { GoogleMap, MarkerF, PolylineF } from '@react-google-maps/api';
import {
  Alert,
  Button,
  Card,
  CircularProgress,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Slider,
  Snackbar,
  TextField,
  Typography
} from '@mui/material';
import useFleetGoogleMaps from '../utils/useFleetGoogleMaps';
import './AssignedRoute.css';
const DEFAULT_CENTER = { lat: 19.076, lng: 72.8777 };
const MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#e9f0e2' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#4b5563' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#e9f0e2' }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#cbd5e1' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#d9e7d0' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#64748b' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#cfe0c4' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#a7b89d' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#374151' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#c7d8bd' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#cbd5e1' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#b8d3ea' }] }
];

function pad2(n) {
  return String(n).padStart(2, '0');
}

function toApiTimestampFromDate(d) {
  // The GPS tracking API stores/expects timestamps in UTC, but the
  // datetime-local input gives us a Date representing local wall-clock
  // time. Use the UTC getters here so the string we send actually lines
  // up with when the device really reported, instead of sending local
  // time digits as if they were already UTC.
  const y = d.getUTCFullYear();
  const m = pad2(d.getUTCMonth() + 1);
  const day = pad2(d.getUTCDate());
  const hh = pad2(d.getUTCHours());
  const mm = pad2(d.getUTCMinutes());
  const ss = pad2(d.getUTCSeconds());
  return `${y}${m}${day}${hh}${mm}${ss}`;
}

function toApiTimestampFromDatetimeLocal(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return toApiTimestampFromDate(d);
}

function toDatetimeLocalValue(d) {
  const y = d.getFullYear();
  const m = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  const hh = pad2(d.getHours());
  const mm = pad2(d.getMinutes());
  return `${y}-${m}-${day}T${hh}:${mm}`;
}

function makeRouteId(apiCreatedAt) {
  if (!apiCreatedAt || apiCreatedAt.length < 14) return `RTE_${Date.now()}`;
  return `RTE_${apiCreatedAt.slice(0, 8)}_${apiCreatedAt.slice(8, 14)}`;
}

function makeMarkerSvg({ fill, stroke, text }) {
  const safeText = String(text || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 42 42">
      <circle cx="21" cy="21" r="15" fill="${fill}" stroke="${stroke}" stroke-width="4"/>
      <circle cx="21" cy="21" r="8" fill="#ffffff" opacity="0.95"/>
      <text x="21" y="25" text-anchor="middle" font-size="14" font-family="Roboto, system-ui, -apple-system, Segoe UI" font-weight="800" fill="${fill}">${safeText}</text>
    </svg>
  `.trim();
}

function markerIcon(google, { fill, stroke, text }) {
  const svg = makeMarkerSvg({ fill, stroke, text });
  const url = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  return {
    url,
    scaledSize: new google.maps.Size(34, 34),
    anchor: new google.maps.Point(17, 17)
  };
}

export default function AssignedRoute({ devices = [], user }) {
  const { isLoaded, loadError, googleMapsApiKey } = useFleetGoogleMaps();

  const now = useMemo(() => new Date(), []);
  const [viewMode, setViewMode] = useState('assign'); // 'assign' | 'compilance'
  const [imei, setImei] = useState('');
  const [routeName, setRouteName] = useState('');
  const [toleranceMeters, setToleranceMeters] = useState(40);
  const [startTimeLocal, setStartTimeLocal] = useState(() => toDatetimeLocalValue(now));
  const [endTimeLocal, setEndTimeLocal] = useState(() => toDatetimeLocalValue(new Date(now.getTime() + 9 * 60 * 60 * 1000)));
  const [points, setPoints] = useState([]);
  const [encodedPolyline, setEncodedPolyline] = useState('');
  const [polylinePath, setPolylinePath] = useState([]);
  const [totalDistanceKm, setTotalDistanceKm] = useState(null);
  const [loadingDirections, setLoadingDirections] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [lastAssigned, setLastAssigned] = useState(null); // { imei, routeId } of the most recently assigned route

  // --- compilance panel state ---
  const [compilanceImei, setcompilanceImei] = useState('');
  const [routeHistory, setRouteHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedcompilanceRouteId, setSelectedcompilanceRouteId] = useState('');
  const [compilanceLoading, setcompilanceLoading] = useState(false);
  const [compilanceData, setcompilanceData] = useState(null); // { plannedPath, followingSegments, followingSinglePoints, deviatedSegments, deviatedPoints, routeInfo, summary }

  const mapRef = useRef(null);
  const directionsReqRef = useRef(0);

  const api = useMemo(() => {
    const baseURL = process.env.REACT_APP_ASSIGNROUTE_API_BASE;
    const apiKey = process.env.REACT_APP_API_KEY || '';
    const headers = { 'Content-Type': 'application/json' };
    if (apiKey) headers['x-api-key'] = apiKey;
    const client = axios.create({ baseURL, headers });
    client.interceptors.request.use(async (config) => {
      const token = await getAccessToken();
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
    client.interceptors.response.use(undefined, async (error) => {
      const request = error.config;
      if (error.response?.status === 401 && request && !request._authRetried) {
        request._authRetried = true;
        try {
          const token = await refreshAccessToken();
          request.headers = request.headers || {};
          request.headers.Authorization = `Bearer ${token}`;
          return client(request);
        } catch {
          return Promise.reject(error);
        }
      }
      return Promise.reject(error);
    });
    return client;
  }, []);

  const imeiOptions = useMemo(() => {
    const list = Array.isArray(devices) ? devices : [];
    const uniq = new Map();
    for (const d of list) {
      const id = String(d?.imei || '').trim();
      if (!id) continue;
      if (!uniq.has(id)) uniq.set(id, { imei: id, name: d?.name || 'Vehicle' });
    }
    return Array.from(uniq.values());
  }, [devices]);

  const handleMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const handleMapUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  const clearRoute = useCallback(() => {
    setPoints([]);
    setEncodedPolyline('');
    setPolylinePath([]);
    setTotalDistanceKm(null);
    setLoadingDirections(false);
  }, []);

  const addWaypointAtCenter = useCallback(() => {
    const map = mapRef.current;
    const center = map?.getCenter?.();
    if (!center) {
      setSnack({ open: true, severity: 'info', message: 'Map is not ready yet.' });
      return;
    }
    setPoints((prev) => [...prev, { lat: center.lat(), lng: center.lng() }]);
  }, []);

  const handleMapClick = useCallback((e) => {
    if (!e?.latLng) return;
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setPoints((prev) => [...prev, { lat, lng }]);
  }, []);

  const removePointAt = useCallback((idx) => {
    setPoints((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  useEffect(() => {
    if (!isLoaded) return;
    if (points.length < 2) {
      setEncodedPolyline('');
      setPolylinePath([]);
      setTotalDistanceKm(null);
      setLoadingDirections(false);
      return;
    }

    const reqId = ++directionsReqRef.current;
    setLoadingDirections(true);
    const service = new window.google.maps.DirectionsService();
    const origin = points[0];
    const destination = points[points.length - 1];
    const waypoints = points.slice(1, -1).map((p) => ({ location: p, stopover: false }));

    service.route(
      {
        origin,
        destination,
        waypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
        optimizeWaypoints: false
      },
      (result, status) => {
        if (reqId !== directionsReqRef.current) return;
        if (status !== 'OK' || !result?.routes?.length) {
          setEncodedPolyline('');
          setPolylinePath([]);
          setTotalDistanceKm(null);
          setLoadingDirections(false);
          setSnack({
            open: true,
            severity: 'error',
            message:
              status === 'REQUEST_DENIED'
                ? 'Directions API request denied. Enable Directions API + billing, and allow your domain in API key restrictions.'
                : `Unable to generate a driving route (${status}).`
          });
          return;
        }

        const route = result.routes[0];
        const encoded = route?.overview_polyline?.points || '';
        const legs = Array.isArray(route?.legs) ? route.legs : [];
        const meters = legs.reduce((sum, leg) => sum + (Number(leg?.distance?.value) || 0), 0);
        const km = meters ? Math.round((meters / 1000) * 10) / 10 : 0;
        const decoded =
          encoded && window.google.maps.geometry?.encoding?.decodePath
            ? window.google.maps.geometry.encoding.decodePath(encoded).map((ll) => ({ lat: ll.lat(), lng: ll.lng() }))
            : Array.isArray(route?.overview_path)
              ? route.overview_path.map((ll) => ({ lat: ll.lat(), lng: ll.lng() }))
              : [];

        const fallbackEncoded =
          !encoded && window.google.maps.geometry?.encoding?.encodePath && Array.isArray(route?.overview_path)
            ? window.google.maps.geometry.encoding.encodePath(route.overview_path)
            : !encoded && window.google.maps.geometry?.encoding?.encodePath && decoded.length
              ? window.google.maps.geometry.encoding.encodePath(
                decoded.map((p) => new window.google.maps.LatLng(p.lat, p.lng))
              )
              : '';

        setEncodedPolyline(encoded || fallbackEncoded);
        setPolylinePath(decoded);
        setTotalDistanceKm(km);
        setLoadingDirections(false);

        const map = mapRef.current;
        if (map && decoded.length) {
          const bounds = new window.google.maps.LatLngBounds();
          for (let i = 0; i < decoded.length; i += Math.max(1, Math.floor(decoded.length / 200))) {
            bounds.extend(decoded[i]);
          }
          bounds.extend(decoded[0]);
          bounds.extend(decoded[decoded.length - 1]);
          map.fitBounds(bounds, 80);
        }
      }
    );
  }, [isLoaded, points]);

  useEffect(() => {
    const prev = window.gm_authFailure;
    window.gm_authFailure = () => {
      setSnack({
        open: true,
        severity: 'error',
        message: 'Google Maps authentication failed. Check API key restrictions (HTTP referrers), enabled APIs (Maps, Places, Directions), and billing.'
      });
    };
    return () => {
      window.gm_authFailure = prev;
    };
  }, []);

  // Fetch route history whenever the compilance-panel vehicle changes
  useEffect(() => {
    setSelectedcompilanceRouteId('');
    if (!compilanceImei) {
      setRouteHistory([]);
      return;
    }
    let cancelled = false;
    setLoadingHistory(true);
    api
      .get('/assign-route', { params: { imei: compilanceImei, history: 'true' } })
      .then((res) => {
        if (cancelled) return;
        setRouteHistory(Array.isArray(res.data?.data) ? res.data.data : []);
      })
      .catch(() => {
        if (!cancelled) setRouteHistory([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingHistory(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, compilanceImei]);

  // Fit the shared map to the compilance paths whenever new compilance data arrives
  useEffect(() => {
    if (viewMode !== 'compilance' || !compilanceData) return;
    const map = mapRef.current;
    if (!map || !window.google) return;
    const bounds = new window.google.maps.LatLngBounds();
    compilanceData.plannedPath.forEach((p) => bounds.extend(p));
    compilanceData.followingSegments.forEach((segment) => segment.forEach((p) => bounds.extend(p)));
    compilanceData.deviatedSegments.forEach((segment) => segment.forEach((p) => bounds.extend(p)));
    compilanceData.deviatedPoints.forEach((p) => bounds.extend(p));
    if (!bounds.isEmpty()) map.fitBounds(bounds, 60);
  }, [viewMode, compilanceData]);

  const canAssign = useMemo(() => {
    const hasTimes = Boolean(startTimeLocal) && Boolean(endTimeLocal);
    const polyOk = Boolean(encodedPolyline) || polylinePath.length >= 2;
    return Boolean(imei) && Boolean(routeName.trim()) && hasTimes && points.length >= 2 && polyOk && !saving;
  }, [imei, routeName, startTimeLocal, endTimeLocal, points.length, encodedPolyline, polylinePath.length, saving]);

  const assignDisabledReason = useMemo(() => {
    if (!imei) return 'Select Vehicle ID';
    if (!routeName.trim()) return 'Enter Route name';
    if (!startTimeLocal || !endTimeLocal) return 'Select Start/End time';
    if (points.length < 2) return 'Add START and END points';
    if (!encodedPolyline && polylinePath.length < 2) return 'Generating route…';
    return '';
  }, [encodedPolyline, endTimeLocal, imei, points.length, polylinePath.length, routeName, startTimeLocal]);

  const assignRoute = useCallback(async () => {
    const startTime = toApiTimestampFromDatetimeLocal(startTimeLocal);
    const endTime = toApiTimestampFromDatetimeLocal(endTimeLocal);
    if (!imei) return setSnack({ open: true, severity: 'error', message: 'Select an IMEI.' });
    if (!routeName.trim()) return setSnack({ open: true, severity: 'error', message: 'Enter a route name.' });
    if (!startTime || !endTime) return setSnack({ open: true, severity: 'error', message: 'Select start and end time.' });
    if (points.length < 2) return setSnack({ open: true, severity: 'error', message: 'Add at least START and END points.' });
    const computedEncoded =
      encodedPolyline ||
      (window.google?.maps?.geometry?.encoding?.encodePath && polylinePath.length
        ? window.google.maps.geometry.encoding.encodePath(
          polylinePath.map((p) => new window.google.maps.LatLng(p.lat, p.lng))
        )
        : '');
    if (!computedEncoded) return setSnack({ open: true, severity: 'error', message: 'Route polyline not ready yet.' });

    const createdAt = toApiTimestampFromDate(new Date());
    const routeId = makeRouteId(createdAt);
    const createdBy = (user || localStorage.getItem('authUser') || 'admin').toString();
    const payload = {
      imei: String(imei),
      routeId,
      activeRoute: true,
      analysisStatus: 'pending',
      createdAt,
      createdBy,
      encodedPolyline: computedEncoded,
      endTime,
      routeName: routeName.trim(),
      startTime,
      status: 'assigned',
      toleranceMeters: Number(toleranceMeters),
      totalDistanceKm: Number(totalDistanceKm || 0),
      waypoints: points.map((p, index) => ({
        index,
        lat: Number(p.lat),
        lng: Number(p.lng),
        type: index === 0 ? 'START' : index === points.length - 1 ? 'END' : 'WAYPOINT'
      }))
    };

    setSaving(true);
    try {
      const url = process.env.REACT_APP_ASSIGN_ROUTE_URL || '/assign-route';
      await api.post(url, payload);
      setSnack({ open: true, severity: 'success', message: 'Route assigned successfully.' });
      setLastAssigned({ imei: String(imei), routeId });
      setImei('');
      setRouteName('');
      setToleranceMeters(40);
      setStartTimeLocal(toDatetimeLocalValue(new Date()));
      setEndTimeLocal(toDatetimeLocalValue(new Date(Date.now() + 9 * 60 * 60 * 1000)));
      clearRoute();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to assign route.';
      setSnack({ open: true, severity: 'error', message: String(msg) });
    } finally {
      setSaving(false);
    }
  }, [api, clearRoute, encodedPolyline, endTimeLocal, imei, points, polylinePath, routeName, startTimeLocal, toleranceMeters, totalDistanceKm, user]);

  const pointRows = useMemo(() => {
    return points.map((p, index) => {
      const type = index === 0 ? 'START' : index === points.length - 1 ? 'END' : 'WAYPOINT';
      const badge = type === 'START' ? 'S' : type === 'END' ? 'E' : String(index);
      return {
        key: String(index),
        index,
        type,
        badge,
        lat: Number(p.lat).toFixed(4),
        lng: Number(p.lng).toFixed(4),
        removable: points.length > 1
      };
    });
  }, [points]);

  const opencompilanceView = useCallback(async (targetImei, targetRouteId) => {
    const routeImei = targetImei || lastAssigned?.imei;
    const routeId = targetRouteId || lastAssigned?.routeId;

    if (!routeImei || !routeId) {
      setSnack({ open: true, severity: 'info', message: 'Select a vehicle and route to view compilance.' });
      return;
    }

    setcompilanceLoading(true);
    setcompilanceData(null);

    try {
      const routeRes = await api.get('/assign-route', { params: { imei: routeImei, routeId } });
      const savedRoute = routeRes.data.data;
      const status = savedRoute.analysisStatus;

      // DEBUG: confirms exactly which branch this run will take.
      // Safe to remove once the retry behavior is confirmed working.
      console.log('opencompilanceView: analysisStatus is', status);

      let analysisPoints = [];
      let summaryStats = null;

      // NOTE: "no_gps_data" is included here so that a route whose first
      // import attempt found nothing (e.g. GPS points hadn't arrived yet)
      // can be retried later, once real GPS data exists for its time window,
      // just by clicking "Show compliance" again.
      if (status === 'pending' || status === 'no_gps_data') {
        await api.post('/import-gps', { imei: routeImei, routeId });
        const analyzeRes = await api.post('/analyze-compilance', { imei: routeImei, routeId });
        analysisPoints = analyzeRes.data.analysis || [];
        summaryStats = {
          totalGpsPoints: analyzeRes.data.totalGpsPoints,
          followingPoints: analyzeRes.data.followingPoints,
          deviatedPoints: analyzeRes.data.deviatedPoints,
          followingPercentage: analyzeRes.data.followingPercentage,
          deviationPercentage: analyzeRes.data.deviationPercentage
        };
      } else if (status === 'gps_loaded') {
        const analyzeRes = await api.post('/analyze-compilance', { imei: routeImei, routeId });
        analysisPoints = analyzeRes.data.analysis || [];
        summaryStats = {
          totalGpsPoints: analyzeRes.data.totalGpsPoints,
          followingPoints: analyzeRes.data.followingPoints,
          deviatedPoints: analyzeRes.data.deviatedPoints,
          followingPercentage: analyzeRes.data.followingPercentage,
          deviationPercentage: analyzeRes.data.deviationPercentage
        };
      } else if (status === 'completed') {
        const readRes = await api.get('/get-route-analysis', { params: { routeId } });
        analysisPoints = readRes.data.data || [];

        // Older routes analyzed before summary-caching was added to
        // analyze-compilance won't have followingPercentage/deviationPercentage
        // on the AssignedRoutes item. Compute them here from the raw analysis
        // points as a fallback so the stats container never shows blank dashes
        // just because a route predates that backend change.
        const hasCachedSummary =
          savedRoute.followingPercentage != null && savedRoute.deviationPercentage != null;

        if (hasCachedSummary) {
          summaryStats = {
            totalGpsPoints: savedRoute.totalGpsPoints ?? analysisPoints.length,
            followingPoints: savedRoute.followingPoints,
            deviatedPoints: savedRoute.deviatedPoints,
            followingPercentage: savedRoute.followingPercentage,
            deviationPercentage: savedRoute.deviationPercentage
          };
        } else {
          const total = analysisPoints.length;
          const followingCount = analysisPoints.filter((p) => p.status === 'FOLLOWING').length;
          const deviatedCount = analysisPoints.filter((p) => p.status === 'DEVIATED').length;
          summaryStats = {
            totalGpsPoints: total,
            followingPoints: followingCount,
            deviatedPoints: deviatedCount,
            followingPercentage: total > 0 ? Math.round((followingCount / total) * 10000) / 100 : 0,
            deviationPercentage: total > 0 ? Math.round((deviatedCount / total) * 10000) / 100 : 0
          };
        }
      } else {
        setcompilanceLoading(false);
        setSnack({ open: true, severity: 'info', message: 'No GPS data available yet for this route.' });
        return;
      }

      const plannedPath =
        window.google?.maps?.geometry?.encoding?.decodePath && savedRoute.encodedPolyline
          ? window.google.maps.geometry.encoding
              .decodePath(savedRoute.encodedPolyline)
              .map((ll) => ({ lat: ll.lat(), lng: ll.lng() }))
          : [];

      // Group consecutive points sharing the same status into their own
      // segment — a run of FOLLOWING points becomes one green line, a run
      // of DEVIATED points becomes one red line. A status change always
      // breaks the current segment, so a line never bridges across a
      // deviation (or back). A segment with only one point can't form a
      // line, so it's kept separately as a dot instead.
      const buildSegments = (targetStatus) => {
        const segments = [];
        const singles = [];
        let current = [];
        for (const p of analysisPoints) {
          const point = { lat: Number(p.latitude ?? p.lat), lng: Number(p.longitude ?? p.lng) };
          if (p.status === targetStatus) {
            current.push(point);
          } else if (current.length > 0) {
            if (current.length >= 2) segments.push(current);
            else singles.push(current[0]);
            current = [];
          }
        }
        if (current.length >= 2) segments.push(current);
        else if (current.length === 1) singles.push(current[0]);
        return { segments, singles };
      };

      const following = buildSegments('FOLLOWING');
      const deviated = buildSegments('DEVIATED');

      setcompilanceData({
        plannedPath,
        followingSegments: following.segments,
        followingSinglePoints: following.singles,
        deviatedSegments: deviated.segments,
        deviatedPoints: deviated.singles,
        routeInfo: {
          routeName: savedRoute.routeName,
          imei: savedRoute.imei,
          toleranceMeters: savedRoute.toleranceMeters,
          startTime: savedRoute.startTime,
          endTime: savedRoute.endTime,
          activeRoute: savedRoute.activeRoute,
          totalDistanceKm: savedRoute.totalDistanceKm
        },
        summary: summaryStats
      });
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to load compilance data.';
      setSnack({ open: true, severity: 'error', message: String(msg) });
    } finally {
      setcompilanceLoading(false);
    }
  }, [api, lastAssigned]);

  const clearcompilance = useCallback(() => {
    setcompilanceData(null);
    setSelectedcompilanceRouteId('');
    const map = mapRef.current;
    if (map) {
      map.setCenter(DEFAULT_CENTER);
      map.setZoom(12);
    }
  }, []);

  const mapOptions = useMemo(() => {
    return {
      clickableIcons: false,
      disableDefaultUI: true,
      zoomControl: true,
      fullscreenControl: false,
      streetViewControl: false,
      mapTypeControl: false,
      gestureHandling: 'greedy',
      styles: MAP_STYLES
    };
  }, []);

  return (
    <div className="ar-root">
      <div className="ar-mapWrap">
        {!googleMapsApiKey ? (
          <div className="ar-mapFallback">
            <div className="ar-mapFallbackCard">
              <div className="ar-mapFallbackTitle">Google Maps API key missing</div>
              <div className="ar-mapFallbackSub">Set REACT_APP_GOOGLE_MAPS_KEY in your environment.</div>
            </div>
          </div>
        ) : loadError ? (
          <div className="ar-mapFallback">
            <div className="ar-mapFallbackCard">
              <div className="ar-mapFallbackTitle">Map failed to load</div>
              <div className="ar-mapFallbackSub">{String(loadError?.message || loadError)}</div>
            </div>
          </div>
        ) : !isLoaded ? (
          <div className="ar-mapFallback">
            <div className="ar-mapFallbackCard">
              <CircularProgress size={26} />
              <div className="ar-mapFallbackSub">Loading map…</div>
            </div>
          </div>
        ) : (
          <GoogleMap
            mapContainerClassName="ar-map"
            center={DEFAULT_CENTER}
            zoom={12}
            options={mapOptions}
            onLoad={handleMapLoad}
            onUnmount={handleMapUnmount}
            onClick={viewMode === 'assign' ? handleMapClick : undefined}
          >
            {viewMode === 'assign' ? (
              <>
                {points.map((p, index) => {
                  const type = index === 0 ? 'START' : index === points.length - 1 ? 'END' : 'WAYPOINT';
                  const icon =
                    type === 'START'
                      ? markerIcon(window.google, { fill: '#16a34a', stroke: '#dcfce7', text: 'S' })
                      : type === 'END'
                        ? markerIcon(window.google, { fill: '#dc2626', stroke: '#fee2e2', text: 'E' })
                        : markerIcon(window.google, { fill: '#2563eb', stroke: '#dbeafe', text: String(index) });
                  return (
                    <MarkerF
                      key={`${p.lat}-${p.lng}-${index}`}
                      position={p}
                      icon={icon}
                      onClick={() => removePointAt(index)}
                    />
                  );
                })}

                {polylinePath.length > 0 && (
                  <PolylineF
                    path={polylinePath}
                    options={{
                      strokeColor: '#000000',
                      strokeOpacity: 1,
                      strokeWeight: 4,
                      clickable: false
                    }}
                  />
                )}
              </>
            ) : (
              compilanceData && (
                <>
                  {compilanceData.plannedPath.length > 0 && (
                    <PolylineF
                      path={compilanceData.plannedPath}
                      options={{ strokeColor: '#2563eb', strokeOpacity: 1, strokeWeight: 4, clickable: false }}
                    />
                  )}
                  {compilanceData.followingSegments.map((segment, i) => (
                    <PolylineF
                      key={`following-${i}`}
                      path={segment}
                      options={{ strokeColor: '#16a34a', strokeOpacity: 1, strokeWeight: 4, clickable: false }}
                    />
                  ))}
                  {compilanceData.deviatedSegments.map((segment, i) => (
                    <PolylineF
                      key={`deviated-line-${i}`}
                      path={segment}
                      options={{ strokeColor: '#dc2626', strokeOpacity: 1, strokeWeight: 4, clickable: false }}
                    />
                  ))}
                  {compilanceData.deviatedPoints.map((p, i) => (
                    <MarkerF
                      key={`deviated-${i}`}
                      position={p}
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 6,
                        fillColor: '#dc2626',
                        fillOpacity: 1,
                        strokeWeight: 0
                      }}
                    />
                  ))}
                </>
              )
            )}
          </GoogleMap>
        )}

        {viewMode === 'compilance' && compilanceLoading && (
          <div className="ar-mapFallback ar-mapOverlay">
            <div className="ar-mapFallbackCard">
              <CircularProgress size={26} />
              <div className="ar-mapFallbackSub">Loading compilance data…</div>
            </div>
          </div>
        )}

        {viewMode === 'compilance' && !compilanceLoading && !compilanceData && (
          <div className="ar-mapFallback ar-mapOverlay">
            <div className="ar-mapFallbackCard">
              <div className="ar-mapFallbackTitle">No compilance data yet</div>
              <div className="ar-mapFallbackSub">Select a vehicle and route, then click Show compilance.</div>
            </div>
          </div>
        )}

        <div className="ar-mapHint">
          {viewMode === 'assign' ? 'Click on map to add waypoints' : 'Viewing route compilance'}
        </div>
      </div>

      <div className="ar-panelColumn">
        {viewMode === 'compilance' && (
          <Button
            variant="contained"
            className="ar-backToAssignBtn"
            onClick={() => setViewMode('assign')}
            fullWidth
          >
            ← Assign route
          </Button>
        )}

        {viewMode === 'assign' ? (
          <Card className="ar-panel" elevation={0}>
            <div className="ar-panelHeader">
              <div className="ar-panelHeaderLeft">
                <div className="ar-title">Assign route</div>
                <div className="ar-subtitle">
                  {points.length ? `${Math.max(points.length - 2, 0)} waypoints added` : 'No points added'}
                </div>
              </div>
              <button
                type="button"
                className="ar-ellipsis"
                onClick={() => setSnack({ open: true, severity: 'info', message: 'Tip: Click any marker to remove it.' })}
              >
                …
              </button>
            </div>

            <div className="ar-panelBody">
              <div className="ar-selectGroup">
                <div className="ar-selectHeading">Vehicle ID</div>
                <FormControl fullWidth size="small">
                  <Select
                    value={imei}
                    onChange={(e) => setImei(e.target.value)}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Vehicle ID' }}
                    renderValue={(selected) => {
                      if (!selected) return <span style={{ opacity: 0.6 }}>e.g. TRK-042</span>;
                      const match = imeiOptions.find((d) => d.imei === selected);
                      return match ? `${match.name} · ${match.imei}` : selected;
                    }}
                    slotProps={{ paper: { style: { maxHeight: 320 } } }}
                  >
                    <MenuItem value="" disabled>
                      <span style={{ opacity: 0.6 }}>e.g. TRK-042</span>
                    </MenuItem>
                    {imeiOptions.map((d) => (
                      <MenuItem key={d.imei} value={d.imei}>
                        {d.name} · {d.imei}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <TextField
                fullWidth
                size="small"
                label="Route name"
                value={routeName}
                onChange={(e) => setRouteName(e.target.value)}
                placeholder="Mumbai Delivery Route"
              />

              <div className="ar-sliderRow">
                <div>
                  <Typography className="ar-label" variant="body2">
                    Tolerance corridor
                  </Typography>
                  <Typography className="ar-helper" variant="caption">
                    How far off-route before flagging red
                  </Typography>
                </div>
                <div className="ar-sliderValue">{toleranceMeters} m</div>
              </div>
              <Slider
                value={toleranceMeters}
                min={25}
                max={250}
                step={5}
                onChange={(_, v) => setToleranceMeters(v)}
                className="ar-slider"
              />

              <div className="ar-timeGrid">
                <TextField
                  fullWidth
                  size="small"
                  type="datetime-local"
                  label="Start time"
                  value={startTimeLocal}
                  onChange={(e) => setStartTimeLocal(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  size="small"
                  type="datetime-local"
                  label="End time"
                  value={endTimeLocal}
                  onChange={(e) => setEndTimeLocal(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </div>

              <Divider className="ar-divider" />

              <div className="ar-waypointsHeader">
                <Typography variant="body2" className="ar-label">
                  Waypoints
                </Typography>
                <div className="ar-distanceChip">{totalDistanceKm != null ? `${totalDistanceKm} km total` : '—'}</div>
              </div>

              <div className="ar-waypointList">
                {pointRows.length === 0 ? (
                  <div className="ar-empty">Add points by clicking on the map.</div>
                ) : (
                  pointRows.map((r) => (
                    <div key={r.key} className="ar-waypointRow">
                      <div className={`ar-badge ar-badge-${r.type.toLowerCase()}`}>{r.badge}</div>
                      <div className="ar-waypointText">
                        <div className="ar-waypointCoords">
                          {r.lat},&nbsp;{r.lng}
                        </div>
                      </div>
                      <button type="button" className="ar-removeBtn" onClick={() => removePointAt(r.index)} aria-label="Remove">
                        ×
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="ar-panelFooter">
              <div className="ar-actionsRow">
                <Button variant="outlined" color="inherit" onClick={addWaypointAtCenter} className="ar-btn">
                  Add waypoint
                </Button>
                <Button variant="outlined" color="inherit" onClick={clearRoute} className="ar-btn">
                  Clear route
                </Button>
              </div>

              <Button
                variant="contained"
                disabled={!canAssign}
                onClick={assignRoute}
                className="ar-assignBtn"
                fullWidth
              >
                {saving ? 'Assigning…' : loadingDirections ? 'Generating route…' : 'Assign route to vehicle'}
              </Button>
              <Button
                variant="contained"
                className="ar-viewcompilanceBtn"
                onClick={() => setViewMode('compilance')}
                fullWidth
              >
                View compilance
              </Button>
            </div>
          </Card>
        ) : (
          <Card className="ar-panel" elevation={0}>
            <div className="ar-panelHeader">
              <div className="ar-panelHeaderLeft">
                <div className="ar-title">Route compilance</div>
                <div className="ar-subtitle">Select a vehicle, then a route</div>
              </div>
            </div>

            <div className="ar-panelBody">
              <div className="ar-selectGroup">
                <div className="ar-selectHeading">Vehicle ID</div>
                <FormControl fullWidth size="small">
                  <Select
                    value={compilanceImei}
                    onChange={(e) => setcompilanceImei(e.target.value)}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Vehicle ID' }}
                    renderValue={(selected) => {
                      if (!selected) return <span style={{ opacity: 0.6 }}>e.g. TRK-042</span>;
                      const match = imeiOptions.find((d) => d.imei === selected);
                      return match ? `${match.name} · ${match.imei}` : selected;
                    }}
                    slotProps={{ paper: { style: { maxHeight: 320 } } }}
                  >
                    <MenuItem value="" disabled>
                      <span style={{ opacity: 0.6 }}>e.g. TRK-042</span>
                    </MenuItem>
                    {imeiOptions.map((d) => (
                      <MenuItem key={d.imei} value={d.imei}>
                        {d.name} · {d.imei}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              {compilanceImei && (
                <div className="ar-selectGroup">
                  <div className="ar-selectHeading">Route</div>
                  <FormControl fullWidth size="small" disabled={loadingHistory || routeHistory.length === 0}>
                    <Select
                      value={selectedcompilanceRouteId}
                      onChange={(e) => setSelectedcompilanceRouteId(e.target.value)}
                      displayEmpty
                      inputProps={{ 'aria-label': 'Route' }}
                      renderValue={(selected) => {
                        if (loadingHistory) return <span style={{ opacity: 0.6 }}>Loading routes…</span>;
                        if (!selected) {
                          return (
                            <span style={{ opacity: 0.6 }}>
                              {routeHistory.length ? 'Select a route' : 'No routes for this vehicle'}
                            </span>
                          );
                        }
                        const match = routeHistory.find((r) => r.routeId === selected);
                        return match ? `${match.routeName || match.routeId}${match.activeRoute ? ' · Active' : ''}` : selected;
                      }}
                      slotProps={{ paper: { style: { maxHeight: 320 } } }}
                    >
                      <MenuItem value="" disabled>
                        <span style={{ opacity: 0.6 }}>Select a route</span>
                      </MenuItem>
                      {routeHistory.map((r) => (
                        <MenuItem key={r.routeId} value={r.routeId}>
                          {r.routeName || r.routeId}
                          {r.activeRoute ? ' · Active' : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              )}

              {compilanceData && (
                <div className="ar-compilanceStats">
                  <div className="ar-compilanceStatsHeader">
                    <div className="ar-compilanceStatsTitle">
                      {compilanceData.routeInfo?.routeName || 'Route'}
                    </div>
                    <div className="ar-compilanceStatsSub">
                      {compilanceData.routeInfo?.imei}
                    </div>
                  </div>

                  <div className="ar-compilanceStatsGrid">
                    <div className="ar-statCard ar-statCard-following">
                      <div className="ar-statValue">
                        {compilanceData.summary?.followingPercentage != null
                          ? `${compilanceData.summary.followingPercentage}%`
                          : '—'}
                      </div>
                      <div className="ar-statLabel">Route followed</div>
                    </div>
                    <div className="ar-statCard ar-statCard-deviated">
                      <div className="ar-statValue">
                        {compilanceData.summary?.deviationPercentage != null
                          ? `${compilanceData.summary.deviationPercentage}%`
                          : '—'}
                      </div>
                      <div className="ar-statLabel">Deviated</div>
                    </div>
                  </div>

                  <div className="ar-statTotalRow">
                    <span className="ar-statTotalLabel">Total GPS points analyzed</span>
                    <span className="ar-statTotalValue">
                      {compilanceData.summary?.totalGpsPoints ?? '—'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="ar-panelFooter">
              <Button
                variant="contained"
                className="ar-showcompilanceBtn"
                disabled={!compilanceImei || !selectedcompilanceRouteId || compilanceLoading}
                onClick={() => opencompilanceView(compilanceImei, selectedcompilanceRouteId)}
                fullWidth
              >
                Show compilance
              </Button>
              <Button
                variant="contained"
                className="ar-clearcompilanceBtn"
                disabled={!compilanceData || compilanceLoading}
                onClick={clearcompilance}
                fullWidth
              >
                Clear compilance
              </Button>
            </div>
          </Card>
        )}
      </div>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.severity} variant="filled" sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
